import React from 'react';
import Button from '../../../../components/Atoms/Button';
import Badge from '../../../../components/Atoms/Badge';
import VideoJobStatusBadge from './VideoJobStatusBadge';
import { useI18n } from '../../../../i18n';

/**
 * Per-file results, warnings, the output location, and the recent Redis log —
 * everything the spec asks a finished job to surface.
 */
const VideoJobDetailModal = ({ job, logs, onClose }) => {
  const { t } = useI18n();
  if (!job) return null;

  const totals = job.totals || {};
  const options = job.options || {};
  const extracted = Number(totals.framesExtracted || 0);
  const warnings = job.warnings || [];
  const rejected = job.rejected || [];

  const samplingSummary = options.samplingMode === 'fps'
    ? `${t('videoToImages.options.samplingFps')}: ${options.fps} fps`
    : options.samplingMode === 'nth'
      ? `${t('videoToImages.options.samplingNth')}: ${options.nthFrame}`
      : `${t('videoToImages.options.samplingCount')}: ${options.totalCount}`;

  const resizeSummary = options.resizeMode === 'max'
    ? `${options.maxWidth}×${options.maxHeight}`
    : t('videoToImages.options.resizeKeep');

  return (
    <div className="modal fade show v2i-modal" tabIndex="-1" role="dialog" onClick={onClose}>
      <div className="modal-dialog modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title h5 mb-0">{t('videoToImages.details.heading')}</h5>
            <VideoJobStatusBadge status={job.status} hasWarnings={Boolean(warnings.length)} />
          </div>

          <div className="modal-body">
            <div className="v2i-detail-grid">
              <div>
                <span className="v2i-label">{t('videoToImages.details.output')}</span>
                <code className="v2i-code">{job.outputBasePath}</code>
              </div>
              <div>
                <span className="v2i-label">{t('videoToImages.details.engineUsed')}</span>
                <p className="mb-0">{job.engine || '-'}</p>
              </div>
              <div>
                <span className="v2i-label">{t('videoToImages.details.optionsUsed')}</span>
                <p className="mb-0">
                  {samplingSummary} · {String(options.outputFormat || '').toUpperCase()} q{options.quality} · {resizeSummary}
                </p>
              </div>
              <div>
                <span className="v2i-label">{t('videoToImages.jobs.columnImages')}</span>
                <p className="mb-0">
                  {extracted
                    ? t('videoToImages.details.totalProduced', { count: extracted })
                    : t('videoToImages.details.noneProduced')}
                </p>
              </div>
            </div>

            {!!job.error && (
              <div className="v2i-alert v2i-alert--danger">
                <strong>{t('videoToImages.details.errors')}:</strong> {job.error}
              </div>
            )}

            {!!warnings.length && (
              <div className="v2i-alert v2i-alert--warning">
                <strong>{t('videoToImages.details.warnings')}</strong>
                <ul className="v2i-alert__list">
                  {warnings.map((warning, index) => <li key={index}>{warning}</li>)}
                </ul>
              </div>
            )}

            <h6 className="v2i-section-title">{t('videoToImages.details.perFile')}</h6>
            <div className="v2i-table-wrap">
              <table className="v2i-table">
                <thead>
                  <tr>
                    <th>{t('videoToImages.details.fileName')}</th>
                    <th>{t('videoToImages.details.fileStatus')}</th>
                    <th>{t('videoToImages.details.fileFrames')}</th>
                    <th>{t('videoToImages.details.fileError')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(job.sources || []).map((source, index) => (
                    <tr key={`${source.name}-${index}`}>
                      <td className="v2i-table__primary">{source.name}</td>
                      <td><VideoJobStatusBadge status={source.status} /></td>
                      <td>{source.framesExtracted || 0}</td>
                      <td className="v2i-table__secondary">{source.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!!rejected.length && (
              <>
                <h6 className="v2i-section-title">{t('videoToImages.details.skipped')}</h6>
                <ul className="v2i-skiplist">
                  {rejected.map((entry, index) => (
                    <li key={index}>
                      <Badge variant="neutral" icon="fa-ban">{entry.name}</Badge>
                      <span className="v2i-table__secondary">{entry.reason}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h6 className="v2i-section-title">{t('videoToImages.details.logs')}</h6>
            {logs.length ? (
              <pre className="v2i-log">{logs.join('\n')}</pre>
            ) : (
              <p className="v2i-hint mb-0">{t('videoToImages.details.noLogs')}</p>
            )}
          </div>

          <div className="modal-footer">
            <Button variant="secondary" size="sm" onClick={onClose}>{t('common.close')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoJobDetailModal;
